import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { getAdminToken, fetchAdminMe } from '../../lib/paymentService';
import { auth } from '../../lib/firebase';
import { verifyAdminCustomClaims } from '../../lib/adminAuth';

interface AdminRouteProps {
  children: React.ReactNode;
}

export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [denialReason, setDenialReason] = useState<string>('Access Denied: Administrative credentials required.');

  useEffect(() => {
    let isMounted = true;
    const verifyToken = async () => {
      try {
        if (typeof (auth as any).authStateReady === 'function') {
          await auth.authStateReady();
        }
      } catch {
        // Ignore
      }

      const currentUser = auth.currentUser;
      let firebaseAdminValid = false;
      if (currentUser) {
        try {
          const freshToken = await currentUser.getIdToken();
          if (freshToken) {
            localStorage.setItem('pairup_admin_auth_token_v1', freshToken);
            localStorage.setItem('pairup_admin_token', freshToken);
          }
          const claimResult = await verifyAdminCustomClaims(currentUser, false);
          if (claimResult.isAdmin) {
            firebaseAdminValid = true;
          }
        } catch {
          firebaseAdminValid = false;
        }
      }

      const token = getAdminToken();
      let serverAdminValid = false;
      if (token) {
        try {
          const res = await fetchAdminMe();
          if (res.success && res.admin) {
            serverAdminValid = true;
          }
        } catch {
          serverAdminValid = false;
        }
      }

      if (isMounted) {
        if (serverAdminValid && (firebaseAdminValid || Boolean(currentUser))) {
          setIsAdminAuthenticated(true);
        } else {
          setIsAdminAuthenticated(false);
          setDenialReason(
            currentUser
              ? 'Access Denied: Your account does not have an active server-verified Administrator session or 2FA verification.'
              : 'Access Denied: Administrative credentials required.'
          );
        }
        setIsVerifying(false);
      }
    };

    verifyToken();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-white">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin mb-3" />
        <p className="text-sm font-bold font-mono">Verifying Administrative Privileges...</p>
        <p className="text-xs text-slate-500 font-mono mt-1">Checking server-assigned custom claims & RBAC guard</p>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    // Redirect non-admin users to /admin/login
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{
          from: location,
          reason: denialReason,
        }}
      />
    );
  }

  return <>{children}</>;
};
